import { useEffect, useId, useMemo, useRef, useState, type KeyboardEvent, type Ref } from 'react'
import { useUsers } from '../hooks/useTasks.ts'
import type { User } from '../lib/types.ts'
import styles from './mentionField.module.css'

/**
 * A textarea that suggests people after `@` (comments and task descriptions).
 *
 * The popup is anchored to the field, not to the caret. Placing it at the caret in a textarea means
 * rendering an invisible mirror of the text to measure where the caret actually is — a well-known
 * pile of edge cases (wrapping, scrolling, fonts) for a popup that has at most two entries in it.
 *
 * Whether a handle belongs to a real person is still settled on the server (services/mentions.ts).
 * This only helps somebody type it correctly.
 */

/** The `@word` being typed right before the caret, or null. */
function activeQuery(value: string, caret: number): { query: string; from: number } | null {
  const before = value.slice(0, caret)
  const match = /(?:^|[^\w@])@([\p{L}\p{N}._-]*)$/u.exec(before)
  if (!match) return null
  return { query: (match[1] ?? '').toLowerCase(), from: caret - (match[1]?.length ?? 0) - 1 }
}

function handleOf(user: User): string {
  return user.displayName.toLowerCase().replace(/\s+/g, '')
}

export function MentionField({
  value,
  onChange,
  onKeyDown,
  textareaRef,
  className,
  ...rest
}: {
  value: string
  onChange: (next: string) => void
  onKeyDown?: ((event: KeyboardEvent<HTMLTextAreaElement>) => void) | undefined
  textareaRef?: Ref<HTMLTextAreaElement> | undefined
  className?: string | undefined
  placeholder?: string | undefined
  'aria-label'?: string | undefined
  rows?: number | undefined
  onBlur?: (() => void) | undefined
}): React.JSX.Element {
  const users = useUsers()
  // useId, not a module counter: the field renders in several places at once (drawer description
  // and comment composer), and aria-controls has to point at this instance's list, not the last
  // one mounted.
  const listboxId = useId()
  const inner = useRef<HTMLTextAreaElement | null>(null)
  const [caret, setCaret] = useState(0)
  const [active, setActive] = useState(0)
  const [dismissed, setDismissed] = useState(false)

  const suggest = useMemo(() => {
    if (dismissed) return null
    const found = activeQuery(value, caret)
    if (!found) return null
    const matches = (users.data ?? []).filter((u) => handleOf(u).startsWith(found.query))
    return matches.length > 0 ? { ...found, matches } : null
  }, [value, caret, users.data, dismissed])

  useEffect(() => setActive(0), [suggest?.query])

  const insert = (user: User): void => {
    if (!suggest) return
    const next = `${value.slice(0, suggest.from)}@${handleOf(user)} ${value.slice(caret)}`
    onChange(next)
    setDismissed(true)

    // Caret goes after the inserted name, not to the end of the text.
    const position = suggest.from + handleOf(user).length + 2
    requestAnimationFrame(() => {
      inner.current?.setSelectionRange(position, position)
      setCaret(position)
    })
  }

  const handleKeyDown = (event: KeyboardEvent<HTMLTextAreaElement>): void => {
    if (suggest) {
      if (event.key === 'ArrowDown') {
        event.preventDefault()
        setActive((i) => (i + 1) % suggest.matches.length)
        return
      }
      if (event.key === 'ArrowUp') {
        event.preventDefault()
        setActive((i) => (i - 1 + suggest.matches.length) % suggest.matches.length)
        return
      }
      // Enter accepts the highlighted person — except with a modifier, which means "send" and must
      // keep working while the popup is open.
      if ((event.key === 'Enter' && !event.metaKey && !event.ctrlKey) || event.key === 'Tab') {
        event.preventDefault()
        insert(suggest.matches[active]!)
        return
      }
      if (event.key === 'Escape') {
        event.preventDefault()
        event.stopPropagation()
        setDismissed(true)
        return
      }
    }
    onKeyDown?.(event)
  }

  const syncCaret = (): void => {
    const element = inner.current
    if (element) setCaret(element.selectionStart)
  }

  return (
    <div className={styles.wrap}>
      {/* No <ul>/<li> around the options. A listbox owns its options directly; the <li> in
          between broke that relationship, so assistive tech saw a list of list-items rather than
          a set of choices — the roles were right and the structure they describe was not.
          Options are plain divs, not buttons, because a button inside a listbox is announced as
          a button; selection is driven from the textarea below via aria-activedescendant. */}
      {suggest && (
        <div className={styles.popup} role="listbox" id={listboxId} aria-label="Osoby">
          {suggest.matches.map((user, index) => (
            // Not focusable on purpose: this is the aria-activedescendant pattern, where focus
            // stays in the textarea and the "current" option is named by id. jsx-a11y assumes the
            // roving-focus variant instead and cannot tell the two apart.
            // eslint-disable-next-line jsx-a11y/interactive-supports-focus
            <div
              key={user.id}
              id={`${listboxId}-option-${user.id}`}
              role="option"
              aria-selected={index === active}
              className={`${styles.option}${index === active ? ` ${styles.optionActive}` : ''}`}
              // mousedown, not click: click fires after blur, and by then the textarea has lost
              // the caret position the insertion depends on.
              onMouseDown={(event) => {
                event.preventDefault()
                insert(user)
              }}
            >
              <span className={styles.optionName}>{user.displayName}</span>
              <span className={styles.optionHandle}>@{handleOf(user)}</span>
            </div>
          ))}
        </div>
      )}

      <textarea
        {...rest}
        ref={(element) => {
          inner.current = element
          if (typeof textareaRef === 'function') textareaRef(element)
          else if (textareaRef)
            (textareaRef as { current: HTMLTextAreaElement | null }).current = element
        }}
        className={className}
        // Without these the popup is invisible to a screen reader: the textarea never says that
        // a list opened, which option is current, or where to find it. The list existed; nothing
        // announced it.
        role="combobox"
        aria-expanded={suggest !== null}
        aria-controls={listboxId}
        aria-autocomplete="list"
        {...(suggest?.matches[active]
          ? { 'aria-activedescendant': `${listboxId}-option-${suggest.matches[active].id}` }
          : {})}
        value={value}
        onChange={(event) => {
          setDismissed(false)
          setCaret(event.target.selectionStart)
          onChange(event.target.value)
        }}
        onKeyUp={syncCaret}
        onClick={syncCaret}
        onKeyDown={handleKeyDown}
      />
    </div>
  )
}
